import '../../styles/styles.css';

import { general } from '../../config/general';
import Link from 'next/link';
import Layout from '../../components/Layout';
import ExpandWrapper from '../../components/ExpandWrapper';
import ToggleButton from '../../components/ToggleButton';
import useToggle from '../../lib/hooks/useToggle';
import Quote from '../../components/Quote';
import VideoHero from '../../components/VideoHero';
import AnimateIn from '../../components/AnimateIn';
import hardcodedStaticProps from '../../lib/harcodedStaticProps';

const Expertise = ({featuredProjectsList}) => {
  const [isActiveStrategy, onToggleStrategy] = useToggle(false);
  const [isActiveExecution, onToggleExecution] = useToggle(false);
  const [isActiveAfterCare, onToggleAfterCare] = useToggle(false);

  return (
    <Layout
      metaData={{ data: { title: 'Expertise' } }}
      featuredProjectsList={featuredProjectsList}
    >
      <section className="components">
        <VideoHero
          component={{
            section: 'Expertise',
            title: 'Where brands,<br>meet culture.',
            video: '215563904',
            posterVideo: '215577885'
          }}
        />

        <AnimateIn className="text g-row">
          <h1>
            Your Majesty delivers strategy-led <em>end-to-end</em> design and
            technology for every interface.
            <br />
            <br />
            You work with us to define, build and continuously improve the
            experiences people have with your product or brand.
          </h1>
        </AnimateIn>

        <AnimateIn className="text g-row">
          <h2>Taking the smart route</h2>

          <p>
            Tailor-made workshops (loved by all our clients) are the first steps
            in collectively understanding your place in culture, your team and
            your business ambitions.
          </p>
          <p>
            A clear and effective roadmap is defined for your goals, whether
            it’s a content, product, or launch strategy. With the undeniable
            benefit of prototyping, you’ll be equipped to quickly test, validate
            and iterate on ideas with real people, which in turn eliminates
            risk, late surprises, and heavy documentation up front.
          </p>
          <p>
            Learn about our work with{' '}
            <Link href="/work/airbnb">
              <a>Airbnb</a>
            </Link>
            ,{' '}
            <Link href="/work/cabiner">
              <a>Cabiner</a>
            </Link>
            , and{' '}
            <Link href="/work/samsung">
              <a>Samsung</a>
            </Link>
            .
          </p>

          <div is="ym-toggle-element">
            <ToggleButton
              expand={'Show examples'}
              collapse={'Hide examples'}
              eventLabel={'Expertise Strategy'}
              isActive={isActiveStrategy}
              onToggle={onToggleStrategy}
              className="mb-25"
            />

            <ExpandWrapper isExpanded={isActiveStrategy}>
              <ul className="list-mini toggle-content__inner">
                <li>
                  You want help to focus and align your team and organization.
                </li>
                <li>You want to meet culture where it’s going.</li>
                <li>You want to define a new opportunity.</li>
                <li>
                  You want to create a meaningful digital product or service
                  your customers really want.
                </li>
                <li>
                  You want to define a roadmap for your brand, product, or
                  utility.
                </li>
              </ul>
            </ExpandWrapper>
          </div>
        </AnimateIn>

        <AnimateIn className="text g-row">
          <h2>Powerful execution</h2>
          <p>
            At your disposal, the production of content, UX, design and
            technology services to all the moments and ways users experience or
            need them.
          </p>
          <p>
            You’ll have a dedicated team ready with the smartest approach for
            your setup whether it’s Agile and sprint-based, Agifall, or
            Waterfall.
          </p>
          <p>
            Like we do it with{' '}
            <Link href="/work/dopper">
              <a>Dopper</a>
            </Link>
            ,{' '}
            <Link href="/work/va-dundee">
              <a>V&A Museum</a>
            </Link>
            , and{' '}
            <Link href="/work/mgm-resorts">
              <a>MGM Resorts</a>
            </Link>
            .
          </p>

          <div is="ym-toggle-element">
            <ToggleButton
              expand={'Show examples'}
              collapse={'Hide examples'}
              eventLabel={'Expertise Execution'}
              isActive={isActiveExecution}
              onToggle={onToggleExecution}
              className="mb-25"
            />

            <ExpandWrapper isExpanded={isActiveExecution}>
              <ul className="list-mini">
                <li>
                  You want a new digital-first brand or redesign for all
                  touchpoints.
                </li>
                <li>
                  You want a digital styleguide for global brand alignment.
                </li>
                <li>You want to launch your brand in a new market.</li>
                <li>
                  You want a platform build that will enable a product or
                  service ecosystem.
                </li>
                <li>
                  You want an eCommerce platform and the expertise to measure
                  its performance.
                </li>
                <li>You want to increase conversion.</li>
                <li>
                  You have a startup and need a team to accelerate the design,
                  UX and technology.
                </li>
                <li>You want a results driven redesign.</li>
                <li>
                  You want a partner with an eye for detail that can deliver
                  quality code.
                </li>
              </ul>

              <Quote
                className={'full quote--inline'}
                component={{
                  quote:
                    'Nobody I have worked with outside of Google has embraced our tools and code requirements the way you have. Truly amazing work, thank you so much for your dedication!',
                  quotee: 'Mike Rigoli, Senior Webmaster/Manager, Google'
                }}
              />

              <ul className="list-mini">
                <li>You want to improve your product/website user journey.</li>
                <li>
                  You want to launch a product and need strategic help and a
                  team that can execute.
                </li>
                <li>You want a magical experience for an event.</li>
                <li>
                  You want a connected utility experience for your business or
                  space.
                </li>
                <li>
                  You want to activate visitors - turning them into customers.
                </li>
              </ul>
            </ExpandWrapper>
          </div>
        </AnimateIn>

        <AnimateIn className="text g-row">
          <h2>In every end, a new beginning</h2>
          <p>
            Delivering an experience doesn’t end with a successful launch.
            That’s when it begins.
          </p>
          <p>
            We help measure and iterate to ensure that your business is
            continuously providing value and meeting culture wherever it’s
            going.
          </p>
          <p>
            Learn about our work with{' '}
            <Link href="/work/bandstand-newcastle-helix">
              <a>Newcastle Helix</a>
            </Link>
            ,{' '}
            <Link href="/work/volkshotel">
              <a>Volkshotel</a>
            </Link>
            , and{' '}
            <Link href="/work/newmotion">
              <a>NewMotion</a>
            </Link>
            .
          </p>

          <div is="ym-toggle-element">
            <ToggleButton
              expand={'Show examples'}
              collapse={'Hide examples'}
              eventLabel={'Expertise Delivery'}
              isActive={isActiveAfterCare}
              onToggle={onToggleAfterCare}
              className="mb-25"
            />

            <ExpandWrapper isExpanded={isActiveAfterCare}>
              <ul className="list-mini toggle-content__inner">
                <li>Quality Assurance</li>
                <li>Launch</li>
                <li>Integration Support</li>
                <li>Staff Training &amp; Support</li>
                <li>Analytics &amp; Insights</li>
                <li>A/B and Multivariate Testing</li>
                <li>Optimization</li>
                <li>Monitoring</li>
                <li>Quantitative and Qualitative Analysis</li>
              </ul>
            </ExpandWrapper>
          </div>
        </AnimateIn>

        <AnimateIn className="text g-row">
          <p>
            Big or small, we’re an email away.
            <br />
            <a href={general.offices[0].contact.href}>Get in touch</a>
            <br />
          </p>
        </AnimateIn>
      </section>
    </Layout>
  );
};

export { hardcodedStaticProps as getStaticProps };

export default Expertise;
